import {
  type Constructor,
  type GraphComponent,
  GroupNodeStyle,
  type ICanvasContext,
  IGraph,
  IInputModeContext,
  type INode,
  type IRectangle,
  type IRenderContext,
  type Matrix,
  NodeStyleBase,
  Point,
  type Rect,
  RectangleNodeStyle,
  ShapeNodeStyle,
  SvgVisual,
  SvgVisualGroup,
  type TaggedSvgVisual
} from '@yfiles/yfiles';
import { getOrientation } from './util.ts';
import { defaultNodeHeight, nodeHeightExaggeration } from './styles.ts';

class RenderDataCache {
  public layout: Rect;
  public height: number;
  public cornerRadius: number;
  public projection: Matrix;

  constructor(layout: Rect, height: number, cornerRadius: number, projection: Matrix) {
    this.layout = layout;
    this.projection = projection;
    this.height = height;
    this.cornerRadius = cornerRadius;
  }
}

type MyVisual = SvgVisualGroup & { svgElement: SVGGElement; tag: RenderDataCache };
export type SupportedNodeStyle = ShapeNodeStyle | RectangleNodeStyle | GroupNodeStyle;

/**
 * A node style that visualizes a node in Pseudo-3D perspective.
 */
export class Pseudo3DNodeStyle extends NodeStyleBase<MyVisual> {
  public readonly wrappedNodeStyle: SupportedNodeStyle;

  constructor(wrappedNodeStyle: SupportedNodeStyle) {
    super();
    this.wrappedNodeStyle = wrappedNodeStyle;
  }

  /**
   * Creates the visual representation for the given node.
   */
  protected createVisual(context: IRenderContext, node: INode): MyVisual {
    const orientation = getOrientation(context.canvasComponent as GraphComponent);

    const cornerRadius = getCornerRadius(this.wrappedNodeStyle);
    const nodeHeight = getNodeHeight(node);
    const nodeElevation = getNodeElevation(context, node);

    const cache = new RenderDataCache(
      node.layout.toRect(),
      nodeHeight,
      cornerRadius,
      context.projection
    );

    const translationLength = getVisualTranslation(nodeHeight, orientation.inclination);

    const visual = this.wrappedNodeStyle.renderer
      .getVisualCreator(node, this.wrappedNodeStyle)
      .createVisual(context)!;

    const heightVector = calculateHeightVector(context.projection);
    const translation = heightVector.multiply(translationLength);

    const originalGroup = new SvgVisualGroup();
    originalGroup.add(visual as SvgVisual);
    SvgVisual.setTranslate(originalGroup.svgElement, translation.x, translation.y);

    const group = new SvgVisualGroup() as MyVisual;
    group.add(originalGroup);

    const corners = getCorners(orientation.rotation);
    const segments = corners.flatMap((corner, index) => {
      return getPathSegment(
        context,
        corner,
        cornerRadius,
        index === 0 ? 'start' : index === corners.length - 1 ? 'end' : 'middle',
        node
      );
    });

    const start = segments[0].p1.add(translation);
    const pathData = `M ${start.x} ${start.y} ${segments.map((s) => s.d).join('')} l ${translation.x} ${translation.y} `;

    const frontFaces = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    frontFaces.setAttribute('d', pathData);
    this.wrappedNodeStyle.stroke?.applyTo(frontFaces, context);
    frontFaces.style.fill = 'white';

    const extraLines = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    extraLines.setAttribute(
      'd',
      segments
        .map(
          (s) =>
            `M ${s.p1.x} ${s.p1.y} l ${translation.x} ${translation.y} M ${s.p2.x} ${s.p2.y} l ${translation.x} ${translation.y}`
        )
        .join('')
    );
    extraLines.style.fill = 'none';
    this.wrappedNodeStyle.stroke?.applyTo(extraLines, context);
    extraLines.style.strokeOpacity = '0.2';

    group.children.insert(0, new SvgVisual(frontFaces));
    group.children.insert(1, new SvgVisual(extraLines));

    const groupElement = group.svgElement;
    const elevationLength = getVisualTranslation(nodeElevation, orientation.inclination);
    const elevation = heightVector.multiply(elevationLength);
    SvgVisual.setTranslate(groupElement, elevation.x, elevation.y);

    group.tag = cache;

    return group;
  }

  protected updateVisual(
    context: IRenderContext,
    oldVisual: MyVisual,
    node: INode
  ): MyVisual | null {
    const oldCache = oldVisual.tag;
    const newCache = new RenderDataCache(
      node.layout.toRect(),
      node.tag?.height || 10,
      getCornerRadius(this.wrappedNodeStyle),
      context.projection
    );

    const originalGroup = oldVisual.children.get(2) as SvgVisualGroup;
    const oldNestedVisual = originalGroup.children.get(0);

    const newNestedVisual = this.wrappedNodeStyle.renderer
      .getVisualCreator(node, this.wrappedNodeStyle)
      .updateVisual(context, oldNestedVisual) as SvgVisual;
    if (newNestedVisual !== oldNestedVisual) {
      originalGroup.children.set(2, newNestedVisual);
    }

    if (
      oldCache.layout.equals(newCache.layout) &&
      oldCache.height === newCache.height &&
      oldCache.cornerRadius === newCache.cornerRadius &&
      oldCache.projection.hasSameValue(newCache.projection)
    ) {
      return oldVisual;
    }

    return this.createVisual(context, node);
  }

  protected lookup(node: INode, type: Constructor): any {
    return this.wrappedNodeStyle.renderer.getContext(node, this.wrappedNodeStyle).lookup(type);
  }

  protected isHit(context: IInputModeContext, location: Point, node: INode): boolean {
    const { inclination } = getOrientation(context.canvasComponent as GraphComponent);
    const nodeHeight = getNodeHeight(node);
    const nodeElevation = getNodeElevation(context, node);
    const heightVector = calculateHeightVector(context.canvasComponent.projection);

    const newLocation = location.subtract(
      heightVector.multiply(getVisualTranslation(nodeHeight + nodeElevation, inclination))
    );

    return this.wrappedNodeStyle.renderer
      .getHitTestable(node, this.wrappedNodeStyle)
      .isHit(context, newLocation);
  }

  protected getBounds(context: ICanvasContext, node: INode): Rect {
    return super.getBounds(context, node);
  }
}

/**
 * Calculates a vector in world coordinates whose transformation by the projection results
 * in the vector (0, -1).
 * @param projection The projection to consider.
 * @returns The vector in world coordinates that gets transformed to the vector (0, -1).
 */
export function calculateHeightVector(projection: Matrix): Point {
  // TODO: cache
  const matrix = projection.clone();
  matrix.invert();
  return matrix.transform(new Point(0, -1));
}

type Corner = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight';

function getCorners(rotation: number): Corner[] {
  if (rotation == 0 || rotation == 360) {
    return ['bottomLeft', 'bottomRight'];
  }
  if (rotation > 0 && rotation < 90) {
    return ['bottomLeft', 'bottomRight', 'topRight'];
  }
  if (rotation == 90) {
    return ['bottomRight', 'topRight'];
  }
  if (rotation > 90 && rotation < 180) {
    return ['bottomRight', 'topRight', 'topLeft'];
  }
  if (rotation == 180) {
    return ['topRight', 'topLeft'];
  }
  if (rotation > 180 && rotation < 270) {
    return ['topRight', 'topLeft', 'bottomLeft'];
  }
  if (rotation == 270) {
    return ['topLeft', 'bottomLeft'];
  }
  if (rotation > 270 && rotation < 360) {
    return ['topLeft', 'bottomLeft', 'bottomRight'];
  }

  return [];
}

function getTangentPoint(
  context: IRenderContext,
  corner: Corner,
  cornerRadius: number,
  layout: IRectangle,
  mode: 'start' | 'end'
): Point {
  const cornerPosition = layout[corner];
  let arcCenter: Point;
  switch (corner) {
    case 'topLeft':
      arcCenter = new Point(cornerPosition.x + cornerRadius, cornerPosition.y + cornerRadius);
      break;
    case 'topRight':
      arcCenter = new Point(cornerPosition.x - cornerRadius, cornerPosition.y + cornerRadius);
      break;
    case 'bottomLeft':
      arcCenter = new Point(cornerPosition.x + cornerRadius, cornerPosition.y - cornerRadius);
      break;
    case 'bottomRight':
      arcCenter = new Point(cornerPosition.x - cornerRadius, cornerPosition.y - cornerRadius);
      break;
  }

  // TODO: Cache
  const projectedVector = new Point(cornerRadius, 0);
  const inverseProjection = context.projection.clone();
  inverseProjection.invert();
  const worldVector = inverseProjection.transform(projectedVector);

  if (mode === 'start') {
    return arcCenter.subtract(worldVector);
  } else {
    return arcCenter.add(worldVector);
  }
}

function getArcStartPoint(corner: Corner, cornerRadius: number, layout: IRectangle): Point {
  const cornerPosition = layout[corner];
  switch (corner) {
    case 'topLeft':
      return new Point(cornerPosition.x + cornerRadius, cornerPosition.y);
    case 'topRight':
      return new Point(cornerPosition.x, cornerPosition.y + cornerRadius);
    case 'bottomLeft':
      return new Point(cornerPosition.x, cornerPosition.y - cornerRadius);
    case 'bottomRight':
      return new Point(cornerPosition.x - cornerRadius, cornerPosition.y);
  }
}

function getArcEndPoint(corner: Corner, cornerRadius: number, layout: IRectangle) {
  const cornerPosition = layout[corner];
  switch (corner) {
    case 'topLeft':
      return new Point(cornerPosition.x, cornerPosition.y + cornerRadius);
    case 'topRight':
      return new Point(cornerPosition.x - cornerRadius, cornerPosition.y);
    case 'bottomLeft':
      return new Point(cornerPosition.x + cornerRadius, cornerPosition.y);
    case 'bottomRight':
      return new Point(cornerPosition.x, cornerPosition.y - cornerRadius);
  }
}

function getPathSegment(
  context: IRenderContext,
  corner: Corner,
  cornerRadius: number,
  mode: 'start' | 'middle' | 'end',
  { layout }: INode
): { d: string; p1: Point; p2: Point } {
  let p1: Point;
  let p2: Point;
  switch (mode) {
    case 'start':
      p1 = getTangentPoint(context, corner, cornerRadius, layout, mode);
      p2 = getArcEndPoint(corner, cornerRadius, layout);
      return {
        d: `L ${p1.x} ${p1.y} A ${cornerRadius} ${cornerRadius} 0 0 0 ${p2.x} ${p2.y}`,
        p1,
        p2
      };
    case 'middle':
      p1 = getArcStartPoint(corner, cornerRadius, layout);
      p2 = getArcEndPoint(corner, cornerRadius, layout);
      return {
        d: `L ${p1.x} ${p1.y} A ${cornerRadius} ${cornerRadius} 0 0 0 ${p2.x} ${p2.y}`,
        p1,
        p2
      };
    case 'end':
      p1 = getArcStartPoint(corner, cornerRadius, layout);
      p2 = getTangentPoint(context, corner, cornerRadius, layout, mode);
      return {
        d: `L ${p1.x} ${p1.y} A ${cornerRadius} ${cornerRadius} 0 0 0 ${p2.x} ${p2.y}`,
        p1,
        p2
      };
  }
}

export function getNodeElevation(context: ICanvasContext, node: INode) {
  if (node.tag?.elevation !== undefined) {
    return node.tag.elevation;
  }
  const graph = context.canvasComponent.lookup(IGraph);
  if (!graph) {
    return 0;
  }
  return getCumulativeHeight(graph, node);
}

export function getNodeHeight(node: INode): number {
  if (node.tag?.height !== undefined) {
    return node.tag.height;
  }
  return defaultNodeHeight;
}

function getCumulativeHeight(graph: IGraph, node: INode): number {
  if (!graph.isGroupNode(node)) {
    return 0;
  }

  return (
    Math.max(...graph.getChildren(node).map((child) => getCumulativeHeight(graph, child))) -
    getNodeHeight(node)
  );
}

export function getVisualTranslation(nodeHeight: number, inclination: number) {
  return nodeHeight * (Math.sin((-inclination * Math.PI) / 180) + 1) * nodeHeightExaggeration;
}

export function getCornerRadius(style: SupportedNodeStyle): number {
  if (style instanceof GroupNodeStyle) {
    return style.cornerRadius;
  } else if (style instanceof RectangleNodeStyle) {
    if (style.scaleCornerSize) {
      throw new Error('Scaled corner size not supported by this style.');
    } else {
      return style.cornerSize;
    }
  } else {
    // we assume a standard round rectangle when no parameter is provided
    return 10;
  }
}

/**
 * Returns whether a node's style is supported by the Pseudo3DNodeStyle.
 */
export function isSupportedStyle(node: INode) {
  let style = node.style;
  return (
    style instanceof GroupNodeStyle ||
    style instanceof ShapeNodeStyle ||
    style instanceof RectangleNodeStyle
  );
}
